import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    useTheme,
    Alert,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    FormControlLabel,
    Switch,
} from '@mui/material';
import { Save, RestartAlt, Palette } from '@mui/icons-material';
import { BasicButton } from '../../../../../components/BasicButton';
import apiClient from '../../../../../services/api/ApiClient';
import { ApiEndpoints } from '../../../../../config/constants';
import { PermissionWrapper } from '../../../../../components/PermissionWrapper';

interface LogoTintColorPickerProps {
    currentColor: string | null;
    onColorChange: (newColor: string | null) => void;
    onPreviewChange?: (previewColor: string | null) => void;
}

// Preset colors for quick selection
const PRESET_COLORS = [
    { name: 'White', value: '#FFFFFF' },
    { name: 'Black', value: '#000000' },
    { name: 'Light Gray', value: '#E0E0E0' },
    { name: 'Dark Gray', value: '#424242' },
    { name: 'Purple', value: '#800080' },
    { name: 'Blue', value: '#1976d2' },
    { name: 'Teal', value: '#009688' },
    { name: 'Gold', value: '#FFD700' },
    { name: 'Red', value: '#d32f2f' },
    { name: 'Green', value: '#388e3c' },
];

export const LogoTintColorPicker: React.FC<LogoTintColorPickerProps> = ({
    currentColor,
    onColorChange,
    onPreviewChange
}) => {
    const theme = useTheme();
    const [isSaving, setIsSaving] = useState(false);
    const [color, setColor] = useState<string>(currentColor || '#FFFFFF');
    const [tintEnabled, setTintEnabled] = useState(!!currentColor);
    const [hasChanges, setHasChanges] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Sync with parent prop
    useEffect(() => {
        setTintEnabled(!!currentColor);
        if (currentColor) {
            setColor(currentColor);
        }
    }, [currentColor]);

    const handleTintToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        const enabled = event.target.checked;
        setTintEnabled(enabled);
        setHasChanges(true);
        // Notify parent immediately for live preview
        onPreviewChange?.(enabled ? color : null);
    };

    const handleColorChange = (newColor: string) => {
        setColor(newColor);
        setHasChanges(true);
        // Notify parent immediately for live preview
        if (tintEnabled) {
            onPreviewChange?.(newColor);
        }
    };

    const handlePresetSelect = (_event: React.MouseEvent<HTMLElement>, selectedColor: string | null) => {
        if (selectedColor) {
            handleColorChange(selectedColor);
        }
    };

    const handleCustomColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        // Validate hex color
        if (/^#[0-9A-Fa-f]{6}$/.test(value) || /^#[0-9A-Fa-f]{3}$/.test(value)) {
            handleColorChange(value);
        } else if (value.startsWith('#') && value.length <= 7) {
            // Allow typing
            setColor(value);
            setHasChanges(true);
        }
    };

    const handleSave = async () => {
        // Validate color format if tint is enabled
        if (tintEnabled && !/^#[0-9A-Fa-f]{6}$/.test(color) && !/^#[0-9A-Fa-f]{3}$/.test(color)) {
            setError('Please enter a valid hex color (e.g., #FFFFFF)');
            return;
        }

        setIsSaving(true);
        setError(null);
        setSuccess(null);

        try {
            await apiClient.patch(
                ApiEndpoints.SplashScreen.UpdateSettings,
                { splashLogoTintColor: tintEnabled ? color : null }
            );

            onColorChange(tintEnabled ? color : null);
            setSuccess('Logo tint color saved successfully!');
            setHasChanges(false);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save logo tint color');
        } finally {
            setIsSaving(false);
        }
    };

    const handleReset = async () => {
        if (!window.confirm('Remove the logo tint color?')) {
            return;
        }

        setIsSaving(true);
        setError(null);
        setSuccess(null);

        try {
            await apiClient.patch(
                ApiEndpoints.SplashScreen.UpdateSettings,
                { splashLogoTintColor: null }
            );

            setTintEnabled(false);
            onColorChange(null);
            setSuccess('Logo tint removed!');
            setHasChanges(false);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to remove logo tint');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            {/* Alerts */}
            {error && (
                <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity='success' sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Action Buttons */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                {!tintEnabled && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        bgcolor: theme.palette.info.main + '20',
                        color: theme.palette.info.main,
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: '0.7rem',
                        mr: 'auto'
                    }}>
                        No Tint Applied
                    </Box>
                )}
                {tintEnabled && currentColor && (
                    <PermissionWrapper screen='splash-screen' permission='update'>
                        <BasicButton
                            title='Remove Tint'
                            variant='outlined'
                            onClick={handleReset}
                            isLoading={isSaving}
                            startIcon={<RestartAlt fontSize='small' />}
                            sx={{
                                height: 32,
                                fontSize: '0.75rem',
                                px: 2,
                            }}
                        />
                    </PermissionWrapper>
                )}
                {hasChanges && (
                    <PermissionWrapper screen='splash-screen' permission='update'>
                        <BasicButton
                            title='Save'
                            variant='contained'
                            onClick={handleSave}
                            isLoading={isSaving}
                            startIcon={<Save fontSize='small' />}
                            sx={{
                                height: 32,
                                fontSize: '0.75rem',
                                px: 2,
                            }}
                        />
                    </PermissionWrapper>
                )}
            </Box>

            {/* Enable Tint Toggle */}
            <Box sx={{ mb: 2 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={tintEnabled}
                            onChange={handleTintToggle}
                            color='primary'
                        />
                    }
                    label={
                        <Typography variant='body2' sx={{ fontFamily: 'primaryFont' }}>
                            Apply tint color to logo
                        </Typography>
                    }
                />
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block', ml: 6, fontFamily: 'primaryFont' }}>
                    Useful for monochrome logos that need to match your theme
                </Typography>
            </Box>

            {/* Color Controls - Only show when enabled */}
            {tintEnabled && (
                <>
                    {/* Color Preview */}
                    <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                            sx={{
                                width: 60,
                                height: 60,
                                borderRadius: 2,
                                bgcolor: color,
                                border: `2px solid ${theme.palette.divider}`,
                                boxShadow: `0 4px 12px ${color}40`,
                            }}
                        />
                        <Box>
                            <Typography variant='body2' sx={{ fontFamily: 'primaryFont', fontWeight: 600 }}>
                                Logo Tint Color
                            </Typography>
                            <Typography variant='caption' sx={{ color: theme.palette.text.secondary, fontFamily: 'primaryFont' }}>
                                {color.toUpperCase()}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Preset Colors */}
                    <Box sx={{ mb: 3 }}>
                        <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ mb: 1, display: 'block', fontFamily: 'primaryFont' }}
                        >
                            Quick Colors
                        </Typography>
                        <ToggleButtonGroup
                            value={color}
                            exclusive
                            onChange={handlePresetSelect}
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                                '& .MuiToggleButtonGroup-grouped': {
                                    border: `1px solid ${theme.palette.divider} !important`,
                                    borderRadius: '8px !important',
                                    m: 0.25,
                                }
                            }}
                        >
                            {PRESET_COLORS.map((preset) => (
                                <Tooltip key={preset.value} title={preset.name} arrow>
                                    <ToggleButton
                                        value={preset.value}
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            p: 0,
                                            bgcolor: preset.value,
                                            '&:hover': {
                                                bgcolor: preset.value,
                                                opacity: 0.8,
                                            },
                                            '&.Mui-selected': {
                                                bgcolor: preset.value,
                                                border: `2px solid ${theme.palette.primary.main} !important`,
                                                boxShadow: `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${theme.palette.primary.main}`,
                                            },
                                        }}
                                    >
                                        <Box sx={{ width: '100%', height: '100%' }} />
                                    </ToggleButton>
                                </Tooltip>
                            ))}
                        </ToggleButtonGroup>
                    </Box>

                    {/* Custom Color Input */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Palette fontSize='small' color='action' />
                            <Typography variant='caption' color='text.secondary' sx={{ fontFamily: 'primaryFont' }}>
                                Custom Color
                            </Typography>
                        </Box>
                        <TextField
                            size='small'
                            value={color}
                            onChange={handleCustomColorChange}
                            placeholder='#FFFFFF'
                            sx={{
                                width: 120,
                                '& .MuiInputBase-input': {
                                    fontFamily: 'monospace',
                                    fontSize: '0.875rem',
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <Box
                                        sx={{
                                            width: 20,
                                            height: 20,
                                            borderRadius: 0.5,
                                            bgcolor: color,
                                            mr: 1,
                                            border: `1px solid ${theme.palette.divider}`,
                                        }}
                                    />
                                ),
                            }}
                        />
                        <input
                            type='color'
                            value={color}
                            onChange={(e) => handleColorChange(e.target.value)}
                            style={{
                                width: 36,
                                height: 36,
                                padding: 0,
                                border: `2px solid ${theme.palette.divider}`,
                                borderRadius: 8,
                                cursor: 'pointer',
                                backgroundColor: 'transparent',
                            }}
                        />
                    </Box>
                </>
            )}
        </>
    );
};
